/**
 * پنل مدیریت — درگاه پذیرندگی رمزارز
 * ====================================
 * ساخت کلید API برای اتصال WHMCS، و دیدن فاکتورهای پرداخت‌شده.
 * پول همه به موجودیِ همین حسابِ مدیر می‌نشیند.
 */
import { useEffect, useState } from 'react'
import { KeyRound, Plus, Power, Copy } from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Panel, DataTable, RefreshButton, Tag } from '../ui'
import { get, post } from '../../lib/api'
import { toast } from '../../store'
import { num, faDate, asArray } from '../../lib/format'

function CopyBtn({ text }: { text: string }) {
  return (
    <button onClick={() => { navigator.clipboard?.writeText(text); toast.ok('کپی شد') }}
      className="btn-ghost !px-2 !py-1 text-[11px]"><Copy className="h-3 w-3" /></button>
  )
}

export default function Commerce() {
  const [keys, setKeys] = useState<any[] | null>(null)
  const [charges, setCharges] = useState<any[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [label, setLabel] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      get('/admin/finance/commerce/keys').then((r:any)=>setKeys(asArray(r))).catch(() => setKeys([])),
      get('/admin/finance/commerce/charges').then((r: any) => setCharges(asArray(r,'data'))).catch(() => setCharges([])),
    ]).finally(() => setLoading(false))
  }
  useEffect(load, [])

  const makeKey = async () => {
    try {
      await post('/admin/finance/commerce/keys', { label })
      toast.ok('کلید ساخته شد'); setLabel(''); load()
    } catch (e: any) { toast.err(e.message) }
  }

  return (
    <>
      <PageHead title="درگاه پذیرندگی رمزارز"
        subtitle="اتصال WHMCS و سیستم‌های بیرونی برای پرداخت فاکتور با رمزارز — پول به موجودی همین حساب می‌نشیند"
        action={<RefreshButton onClick={load} loading={loading} />} />

      <Panel title="کلیدهای API" dense
        action={
          <div className="flex gap-2">
            <input className="input !py-1.5 text-xs" placeholder="برچسب (مثلاً پاسارگاد میزبان)"
              value={label} onChange={(e) => setLabel(e.target.value)} />
            <button onClick={makeKey} className="btn-primary !py-1.5 text-xs">
              <Plus className="h-3.5 w-3.5" /> کلید جدید
            </button>
          </div>
        }>
        {!keys?.length ? (
          <div className="p-4 text-center text-xs text-slate-500">هنوز کلیدی نساخته‌اید.</div>
        ) : (
          <div className="space-y-2 p-2">
            {keys.map((k) => (
              <div key={k.id} className="rounded-xl border border-white/[.07] bg-white/[.02] p-3">
                <div className="mb-2 flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-brand-400" />
                  <span className="text-sm font-bold text-white">{k.label}</span>
                  {k.active ? <Tag kind="up">فعال</Tag> : <Tag kind="muted">غیرفعال</Tag>}
                  <button onClick={async () => {
                    try { await post(`/admin/finance/commerce/keys/${k.id}/toggle`); load() }
                    catch (e: any) { toast.err(e.message) }
                  }} className="btn-ghost ms-auto !px-2 !py-1 text-[11px]"><Power className="h-3 w-3" /></button>
                </div>
                <div className="grid gap-1.5 text-[11px]">
                  <Row label="API Key" value={k.key} />
                  <Row label="Secret (کلید امضا)" value={k.secret} />
                </div>
              </div>
            ))}
            <p className="px-1 text-[11px] leading-6 text-slate-500">
              این دو مقدار را در ماژول WHMCS وارد کنید. Secret را جای امنی نگه دارید؛
              با آن، صحتِ پیام‌های «پرداخت شد» بررسی می‌شود.
            </p>
          </div>
        )}
      </Panel>

      <div className="mt-4">
        <Panel title="فاکتورها" dense>
          <DataTable rows={charges} loading={loading} minWidth={760} empty="فاکتوری ثبت نشده است"
            columns={[
              { key: 'o', header: 'شناسه فاکتور', cell: (c: any) => <span className="num text-slate-200" dir="ltr">{c.orderId}</span> },
              { key: 'a', header: 'مبلغ (تومان)', cell: (c: any) => <span className="num text-slate-300">{num(c.amountIrt)}</span> },
              { key: 'c', header: 'ارز', cell: (c: any) => <span className="num text-slate-300" dir="ltr">{c.symbol ? `${c.symbol} · ${c.network}` : '—'}</span> },
              { key: 'e', header: 'مبلغ کریپتو', cell: (c: any) => <span className="num text-slate-400" dir="ltr">{c.expected || '—'}</span> },
              {
                key: 's', header: 'وضعیت', cell: (c: any) => {
                  const m: Record<string, any> = { paid: 'up', pending: 'gold', underpaid: 'down', expired: 'muted', cancelled: 'muted', new: 'info' }
                  const t: Record<string, string> = { paid: 'پرداخت شد', pending: 'در انتظار', underpaid: 'کسری', expired: 'منقضی', cancelled: 'لغو', new: 'جدید' }
                  return <Tag kind={m[c.status] || 'muted'}>{t[c.status] || c.status}</Tag>
                },
              },
              { key: 'cb', header: 'اعلام به سایت', cell: (c: any) => c.status === 'paid' ? (c.callbackSent ? <Tag kind="up">ارسال شد</Tag> : <Tag kind="gold">در صف</Tag>) : <span className="text-slate-600">—</span> },
              { key: 'd', header: 'تاریخ', cell: (c: any) => <span className="text-slate-500">{faDate(c.createdAt, false)}</span> },
            ]} />
        </Panel>
      </div>
    </>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-slate-500">{label}</span>
      <span className="flex items-center gap-1">
        <span className="num break-all text-[10px] text-slate-200" dir="ltr">{value}</span>
        <CopyBtn text={value} />
      </span>
    </div>
  )
}
